import React from 'react';
import { motion } from 'framer-motion';
import { Line, ResponsiveLine } from '@nivo/line';
import { formatPrice } from '../../utils/formatPrice';
import { ChartBarIcon, CurrencyDollarIcon, ArrowTrendingUpIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const mockEarningsData = [
  { month: 'Jan', earnings: 4200 },
  { month: 'Feb', earnings: 3800 },
  { month: 'Mar', earnings: 5100 },
  { month: 'Apr', earnings: 4700 },
  { month: 'May', earnings: 6200 },
  { month: 'Jun', earnings: 5800 }
];

const EarningsOverview: React.FC = () => {
  const totalEarnings = mockEarningsData.reduce((sum, month) => sum + month.earnings, 0);
  const averageEarnings = totalEarnings / mockEarningsData.length;
  const monthlyGrowth = ((mockEarningsData[5].earnings - mockEarningsData[4].earnings) / mockEarningsData[4].earnings) * 100;
  
  const lineChartData = [
    {
      id: 'earnings',
      data: mockEarningsData.map(d => ({
        x: d.month,
        y: d.earnings
      }))
    }
  ];

  const statCards = [
    {
      title: 'Total Earnings',
      value: formatPrice(totalEarnings),
      change: '+12.5%',
      icon: CurrencyDollarIcon,
      color: 'text-emerald-500'
    },
    {
      title: 'Monthly Average',
      value: formatPrice(averageEarnings),
      change: '+8.2%',
      icon: ChartBarIcon,
      color: 'text-blue-500'
    },
    {
      title: 'Active Guests',
      value: '24',
      change: '+15.3%',
      icon: UserGroupIcon,
      color: 'text-purple-500'
    },
    {
      title: 'Monthly Growth',
      value: `${monthlyGrowth.toFixed(1)}%`,
      change: '+2.4%',
      icon: ArrowTrendingUpIcon,
      color: 'text-amber-500'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-lg p-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors duration-200"
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${stat.color} bg-opacity-10`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                {stat.change}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{stat.title}</h3>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{stat.value}</p>
          </motion.div>
        ))}
      </div>

      <div style={{ height: 300, width: '100%' }}>
        <ResponsiveLine
          data={lineChartData}
          margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: value => `$${value}`
          }}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          curve="monotoneX"
          colors={['#FF385C']}
          enableGridX={false}
          enableGridY={true}
          gridYValues={5}
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 12,
                  fill: '#6B7280'
                }
              }
            },
            grid: {
              line: {
                stroke: '#E5E7EB',
                strokeWidth: 1
              }
            },
            crosshair: {
              line: {
                stroke: '#FF385C',
                strokeWidth: 1,
                strokeOpacity: 0.35
              }
            }
          }}
          motionConfig="gentle"
        />
      </div>
    </motion.div>
  );
};

export default EarningsOverview;